import { motion } from "framer-motion";
import {
  Building2,
  PenLine,
  DoorClosed,
  ArrowRightLeft,
  FileText,
  Landmark,
} from "lucide-react";

const formalites = [
  {
    icon: Building2,
    title: "Création de société",
    body:
      "Vous indiquez la structure retenue. Nous rédigeons les statuts, publions l'annonce légale et organisons l'immatriculation au greffe compétent.",
  },
  {
    icon: PenLine,
    title: "Modifications statutaires",
    body:
      "Changement de dirigeant, transfert de siège, capital, objet ou dénomination : nous préparons les actes, réunissons les pièces et suivons le dépôt.",
  },
  {
    icon: DoorClosed,
    title: "Cessation d'activité",
    body:
      "Dissolution, liquidation, radiation : nous rédigeons les documents, rassemblons les justificatifs et organisons chaque dépôt auprès des services compétents.",
  },
  {
    icon: ArrowRightLeft,
    title: "Cessions",
    body:
      "Cession de fonds de commerce, de parts ou d'actions : nous préparons l'opération, rédigeons les actes et constituons le dossier de formalités.",
  },
  {
    icon: FileText,
    title: "Rédaction d'actes",
    body:
      "Pactes d'associés, procès-verbaux, contrats commerciaux et conventions : chaque document est rédigé à partir des éléments que vous nous transmettez.",
  },
  {
    icon: Landmark,
    title: "Enregistrement au SIE",
    body:
      "Dépôt et suivi de vos actes auprès du Service des Impôts des Entreprises compétent, avec contrôle des pièces et suivi de l'enregistrement.",
  },
];

export default function Formalites() {
  return (
    <section
      id="formalites"
      data-testid="formalites-section"
      className="relative overflow-hidden border-t border-[#1a1918]/15 bg-[#f7f5f0] py-24 md:py-40"
    >
      <div className="absolute inset-0" data-testid="formalites-background">
        <img
          src="https://images.unsplash.com/photo-1659536959956-93b0e33df737?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MjJ8MHwxfHNlYXJjaHwyfHx0cmlidW5hbCUyMGRlJTIwY29tbWVyY2UlMjBwYXJpcyUyMGNvdXJ0fGVufDB8fHx8MTc5MDc3MTkxOXww&ixlib=rb-4.1.0&q=85&w=1800"
          alt="Tribunal de commerce de Paris et son greffe — façade institutionnelle"
          className="h-full w-full scale-105 object-cover blur-sm"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#f7f5f0]/90 via-[#f7f5f0]/70 to-[#f7f5f0]/45" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mb-16 grid grid-cols-12 md:mb-24"
        >
          <div className="col-span-12 md:col-span-3">
            <span data-testid="formalites-kicker" className="section-badge">
              Formalités juridiques
            </span>
          </div>
          <div className="col-span-12 mt-4 md:col-span-8 md:col-start-4 md:mt-0">
            <h2 className="font-serif-ed text-5xl leading-[1] tracking-tight text-[#1a1918] md:text-6xl lg:text-7xl">
              Vous choisissez. Nous préparons{" "}
              <span className="italic text-[#ce1e20]">les actes</span>.
            </h2>
            <p className="mt-8 max-w-3xl text-base text-[#1a1918]/78 md:text-lg">
              Vous retenez la formalité ou la procédure qui vous convient.
              ProSecours rédige les actes, réunit les pièces, demande la date
              auprès du tribunal lorsque nécessaire et organise le dépôt auprès
              du greffe compétent.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
          {formalites.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.article
                key={f.title}
                data-testid={`formalite-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.06 }}
                className="flex min-h-[320px] flex-col bg-white/95 p-8 shadow-sm backdrop-blur-sm md:p-10"
              >
                <span className="mb-8 flex h-12 w-12 items-center justify-center bg-[#0a1e38] text-white">
                  <Icon size={20} strokeWidth={1.4} />
                </span>
                <h3 className="font-serif-ed text-2xl italic leading-tight text-[#1a1918] md:text-3xl">
                  {f.title}
                </h3>
                <p className="mt-5 flex-1 text-sm leading-relaxed text-[#1a1918]/75 md:text-base">
                  {f.body}
                </p>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.9 }}
          className="mt-14 max-w-3xl"
        >
          <p className="text-base leading-relaxed text-[#1a1918]/80 md:text-lg">
            Une fois votre choix indiqué, un devis clair et personnalisé vous
            est adressé avant toute préparation du dossier.
          </p>
          <a
            href="#audit"
            data-testid="formalites-cta"
            className="mt-6 inline-flex items-center gap-2 bg-[#ce1e20] px-5 py-3 text-white overline transition-colors duration-300 hover:bg-[#a71518]"
          >
            Décrire ma formalité
            <span>→</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
